import { DX, DY, type Dir } from './types';
import { T_ROCK, type World } from './world';

export interface Step {
  x: number;
  y: number;
  dir: Dir;
}

/** Binary heap keyed by f-score. */
class Heap {
  private a: number[] = [];
  private f: number[] = [];

  push(node: number, score: number) {
    const a = this.a;
    const f = this.f;
    a.push(node);
    f.push(score);
    let i = a.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (f[p] <= f[i]) break;
      [a[p], a[i]] = [a[i], a[p]];
      [f[p], f[i]] = [f[i], f[p]];
      i = p;
    }
  }

  pop(): number | undefined {
    const a = this.a;
    const f = this.f;
    if (!a.length) return undefined;
    const top = a[0];
    const la = a.pop()!;
    const lf = f.pop()!;
    if (a.length) {
      a[0] = la;
      f[0] = lf;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (l < a.length && f[l] < f[m]) m = l;
        if (r < a.length && f[r] < f[m]) m = r;
        if (m === i) break;
        [a[m], a[i]] = [a[i], a[m]];
        [f[m], f[i]] = [f[i], f[m]];
        i = m;
      }
    }
    return top;
  }

  get size() {
    return this.a.length;
  }
}

export function passable(w: World, x: number, y: number): boolean {
  if (!w.inBounds(x, y)) return false;
  const i = y * w.w + x;
  if (!w.fog[i]) return false;
  if (w.terrain[i] === T_ROCK) return false;
  if (w.occ[i]) return false;
  const z = w.zoneOf(x, y);
  if (z && z.state !== 'approved' && z.state !== 'claimed') return false;
  return true;
}

/**
 * A* over free tiles. Each start has the direction work arrives from, so going
 * straight costs nothing extra. Goals map a tile index to the direction the
 * last belt must point (into the target building).
 */
export function routeBelt(w: World, starts: { x: number; y: number; dir: Dir }[], goals: Map<number, Dir>, maxNodes = 40000): Step[] | null {
  if (!starts.length || !goals.size) return null;
  const W = w.w;
  const goalPts = [...goals.keys()].map((i) => [i % W, Math.floor(i / W)] as const);
  const h = (x: number, y: number) => {
    let best = Infinity;
    for (const [gx, gy] of goalPts) best = Math.min(best, Math.abs(gx - x) + Math.abs(gy - y));
    return best;
  };
  const g = new Map<number, number>();
  const came = new Map<number, number>();
  const open = new Heap();
  for (const s of starts) {
    if (!passable(w, s.x, s.y)) continue;
    const node = (s.y * W + s.x) * 4 + s.dir;
    g.set(node, 1);
    came.set(node, -1);
    open.push(node, 1 + h(s.x, s.y));
  }
  let expanded = 0;
  while (open.size && expanded < maxNodes) {
    const node = open.pop()!;
    expanded++;
    const tile = node >> 2;
    const d = (node & 3) as Dir;
    const x = tile % W;
    const y = Math.floor(tile / W);
    if (goals.has(tile)) {
      const path: number[] = [];
      let n = node;
      while (n !== -1) {
        path.push(n >> 2);
        n = came.get(n)!;
      }
      path.reverse();
      const steps: Step[] = [];
      for (let i = 0; i < path.length; i++) {
        const px = path[i] % W;
        const py = Math.floor(path[i] / W);
        let dir: Dir;
        if (i + 1 < path.length) {
          const nx = path[i + 1] % W;
          const ny = Math.floor(path[i + 1] / W);
          dir = (nx > px ? 0 : nx < px ? 2 : ny > py ? 1 : 3) as Dir;
        } else dir = goals.get(path[i])!;
        steps.push({ x: px, y: py, dir });
      }
      return steps;
    }
    const base = g.get(node)!;
    for (let nd = 0 as Dir; nd < 4; nd = (nd + 1) as Dir) {
      if (nd === ((d + 2) % 4)) continue;
      const nx = x + DX[nd];
      const ny = y + DY[nd];
      if (!passable(w, nx, ny)) continue;
      const cost = base + 1 + (nd === d ? 0 : 0.35);
      const nn = (ny * W + nx) * 4 + nd;
      if (cost < (g.get(nn) ?? Infinity)) {
        g.set(nn, cost);
        came.set(nn, node);
        open.push(nn, cost + h(nx, ny));
      }
    }
  }
  return null;
}
